import {
  Component,
  effect,
  ElementRef,
  EventEmitter,
  inject,
  OnDestroy,
  Output,
  signal,
  ViewChild,
} from '@angular/core';
import { NgTemplateOutlet } from '@angular/common';
import { BrandFetchService, BrandSuggestionResponse } from '../../../onboarding/service/brand-fetch/brand-fetch.service';
import { FormField, form, maxLength, minLength, pattern, required } from '@angular/forms/signals';
import { catchError, debounceTime, EMPTY, finalize, of, Subject, Subscription, switchMap, take } from 'rxjs';
import { OnboardingBrandService } from '../../../onboarding/service/onboarding-brand/onboarding-brand.service';
import { SlickOnboard } from '../../service/slick-onboard/slick-onboard.service';
import { AddImageComponent } from '../../../../atoms/add-image/add-image.component';
import { S3BucketEnum } from '../../../../common/general-types/media-assets.dto';
import { GaService } from '../../../../services/ga/ga';
import { MiscService } from '../../../../services/misc/misc.service';

/** Shape used by brand-details template (Brandfetch response + manual fallback). */
export interface BrandDetailCardIndustry {
  id: string;
  name: string;
  emoji: string;
}

export interface BrandDetailCardLink {
  name: string;
  url?: string;
}

export interface BrandDetailCardModel {
  name?: string;
  domain?: string;
  description?: string;
  longDescription?: string;
  company?: { industries?: BrandDetailCardIndustry[] };
  links?: BrandDetailCardLink[];
}

const MANUAL_BRAND_ID = 'manual-onboarding';
const MANUAL_BRAND_PLACEHOLDER_ICON = '/assets/SHOUT_studio_icon_full_colour.svg';
const LOGO_URL_PATTERN = /^https?:\/\/.+/i;

function marketingAboutFromCard(card: BrandDetailCardModel): string {
  const parts = [card.description, card.longDescription].filter((p) => (p ?? '').trim().length > 0);
  return parts.join('\n\n').trim();
}

/** Narrow Brandfetch.io v2 brand JSON into fields we render. */
function normalizeBrandfetchDetail(raw: unknown): BrandDetailCardModel {
  if (!raw || typeof raw !== 'object') {
    return { company: { industries: [] }, links: [] };
  }
  const r = raw as Record<string, unknown>;

  const description = typeof r['description'] === 'string' ? r['description'] : '';
  const longDescription = typeof r['longDescription'] === 'string' ? r['longDescription'] : '';
  const name = typeof r['name'] === 'string' ? r['name'] : undefined;
  const domain = typeof r['domain'] === 'string' ? r['domain'] : undefined;

  let industries: BrandDetailCardIndustry[] = [];
  const company = r['company'];
  if (company && typeof company === 'object') {
    const indRaw = (company as Record<string, unknown>)['industries'];
    if (Array.isArray(indRaw)) {
      industries = indRaw
        .map((item, i) => {
          if (!item || typeof item !== 'object') return undefined;
          const it = item as Record<string, unknown>;
          const id = String(it['id'] ?? `ind-${i}`);
          const nm = typeof it['name'] === 'string' ? it['name'] : '';
          const emoji = typeof it['emoji'] === 'string' ? it['emoji'] : '';
          return { id, name: nm, emoji };
        })
        .filter((x): x is BrandDetailCardIndustry => x !== undefined && x.name.length > 0);
    }
  }

  let links: BrandDetailCardLink[] = [];
  const linksRaw = r['links'];
  if (Array.isArray(linksRaw)) {
    links = linksRaw.map((item, i) => {
      if (!item || typeof item !== 'object') {
        return { name: `Link ${i + 1}` };
      }
      const L = item as Record<string, unknown>;
      const linkName =
        typeof L['name'] === 'string'
          ? L['name']
          : typeof L['type'] === 'string'
            ? L['type']
            : `Social ${i + 1}`;
      const url = typeof L['url'] === 'string' ? L['url'] : undefined;
      return { name: linkName, url };
    });
  }

  return {
    name,
    domain,
    description,
    longDescription,
    company: { industries },
    links,
  };
}

@Component({
  selector: 'v2-brand-details',
  standalone: true,
  imports: [FormField, AddImageComponent, NgTemplateOutlet],
  templateUrl: './brand-details.html',
  styleUrl: './brand-details.css',
})
export class BrandDetails implements OnDestroy {
  /** Exposed for template checks (manual onboarding path). */
  readonly manualBrandFlowId = MANUAL_BRAND_ID;

  /** Remount `atom-add-image` when `logoUrl` is set without upload (prefill / pasted URL). */
  manualLogoEditorRevision = signal(0);

  readonly S3BucketEnum = S3BucketEnum;

  @Output() stepComplete = new EventEmitter<boolean>();

  @ViewChild('companyWebsiteInput') companyWebsiteInput?: ElementRef<HTMLInputElement>;

  brandFetchModel = signal({
    domain: '',
  });
  brandFetchForm = form(this.brandFetchModel);

  manualBrandEntry = signal(false);
  /** Inline edit on the selected-brand review card. */
  brandReviewEditMode = signal(false);
  /** `api` = picked from Brandfetch search; `manual` = user-entered details. */
  brandSelectionSource = signal<'api' | 'manual' | undefined>(undefined);

  manualBrandModel = signal({
    companyName: '',
    website: '',
    description: '',
    logoUrl: '',
  });
  manualBrandForm = form(this.manualBrandModel, (schemaPath) => {
    required(schemaPath.companyName, { message: 'Brand name is required' });
    minLength(schemaPath.companyName, 2, { message: 'Brand name must be at least 2 characters' });
    maxLength(schemaPath.companyName, 120, { message: 'Brand name is too long' });
    required(schemaPath.website, { message: 'Website is required' });
    minLength(schemaPath.website, 2, { message: 'Enter a valid website' });
    maxLength(schemaPath.website, 253, { message: 'Website is too long' });
    required(schemaPath.description, { message: 'Brand description is required' });
    minLength(schemaPath.description, 10, { message: 'Description must be at least 10 characters' });
    maxLength(schemaPath.description, 4000, { message: 'Description is too long' });
    required(schemaPath.logoUrl, { message: 'Logo image URL is required' });
    pattern(schemaPath.logoUrl, LOGO_URL_PATTERN, {
      message: 'Enter a direct image URL starting with http:// or https://',
    });
    maxLength(schemaPath.logoUrl, 2048, { message: 'URL is too long' });
  });

  brandSuggestions = signal<BrandSuggestionResponse[] | undefined>(undefined);
  brandSearchLoading = signal(false);
  brandDetailFetchPending = signal(false);

  private domainSubject = new Subject<string>();
  private subscriptions = new Subscription();

  selectedBrand = signal<BrandSuggestionResponse | undefined>(undefined);

  brandDetails = signal<BrandDetailCardModel | undefined>(undefined);

  currencyOptions: { label: string; value: string; flag: string }[] = [
    { label: 'USD', value: 'usd', flag: '🇺🇸' },
    { label: 'GBP', value: 'gbp', flag: '🇬🇧' },
    { label: 'EUR', value: 'eur', flag: '🇪🇺' },
  ];

  selectedCurrencyLabel = () => {
    const code = this.slickOnboardService.brandCurrencyCode();
    const opt = this.currencyOptions.find((c) => c.value === code);
    return opt ? `${opt.flag} ${opt.label}` : 'Select currency';
  };

  onCurrencySelected(code: string): void {
    this.slickOnboardService.chooseBrandCurrency(code);
  }

  goalOptions: { id: number; name: string; description: string; color: string }[] = [
    { id: 1, name: 'Brand Awareness', description: 'Increase visibility and reach new audiences', color: 'bg-teal-50 dark:bg-teal-500/10 text-teal-600 dark:text-teal-400' },
    { id: 2, name: 'Product Sales', description: 'Drive purchases and conversions', color: 'bg-blue-50 dark:bg-blue-500/10 text-blue-600 dark:text-blue-400' },
    { id: 3, name: 'App Installs', description: 'Get more users to download your app', color: 'bg-green-50 dark:bg-green-500/10 text-green-600 dark:text-green-400' },
    { id: 4, name: 'Table Bookings', description: 'Fill your restaurant with reservations', color: 'bg-yellow-50 dark:bg-yellow-500/10 text-yellow-600 dark:text-yellow-400' },
    { id: 5, name: 'Subscription Signups', description: 'Grow your subscriber base', color: 'bg-primary/10 dark:bg-primary/15 text-accent' },
    { id: 6, name: 'Lead Generation', description: 'Generate more leads for your business', color: 'bg-red-50 dark:bg-red-500/10 text-red-600 dark:text-red-400' },
    { id: 7, name: 'Event Registrations', description: 'Get more people to register for your events', color: 'bg-orange-50 dark:bg-orange-500/10 text-orange-600 dark:text-orange-400' },
    { id: 8, name: 'Donations', description: 'Raise more funds for your cause', color: 'bg-pink-50 dark:bg-pink-500/10 text-pink-600 dark:text-pink-400' },
  ];

  /** Services */
  private readonly brandFetchService = inject(BrandFetchService);
  public onboardingBrandService = inject(OnboardingBrandService);
  public readonly slickOnboardService = inject(SlickOnboard);
  private readonly gaService = inject(GaService);
  private readonly miscService = inject(MiscService);
  /** Services - END */

  constructor() {
    this.subscriptions.add(
      this.miscService.getCountry().pipe(take(1)).subscribe((country) => {
        this.slickOnboardService.applyDetectedCountry(country);
      }),
    );
    this.subscriptions.add(
      this.domainSubject
        .pipe(
          debounceTime(250),
          switchMap((domain) => {
            const trimmed = domain.trim();
            const normalized = this.normalizeWebsiteInput(trimmed);
            if (normalized !== trimmed) {
              this.brandFetchModel.set({ domain: normalized });
            }
            if (!normalized || normalized.length <= 3) {
              this.brandSearchLoading.set(false);
              this.brandSuggestions.set(undefined);
              return EMPTY;
            }
            this.brandSearchLoading.set(true);
            return this.brandFetchService.brandSearch(normalized).pipe(
              catchError(() => of([] as BrandSuggestionResponse[])),
              finalize(() => this.brandSearchLoading.set(false)),
            );
          }),
        )
        .subscribe((response) => {
          this.brandSuggestions.set(response);

          if (response.length == 1) {
            this.onBrandSelect(response[0]);
          }
        }),
    );

    effect(() => {
      const domain = this.brandFetchModel().domain;
      this.domainSubject.next(domain);
    });
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  /** Normalize user/URL input to a hostname for Brandfetch and storage (no scheme, no path, no leading www). */
  normalizeWebsiteInput(raw: string): string {
    let s = raw.trim().toLowerCase();
    s = s.replace(/^https?:\/\//, '');
    const slash = s.indexOf('/');
    if (slash >= 0) {
      s = s.substring(0, slash);
    }
    return s.replace(/^www\./, '');
  }

  private clearBrandSelectionState(): void {
    this.selectedBrand.set(undefined);
    this.brandDetails.set(undefined);
    this.slickOnboardService.brandDetails.set(undefined);
    this.slickOnboardService.brandMarketingAbout.set('');
    this.brandSelectionSource.set(undefined);
    this.brandDetailFetchPending.set(false);
    this.brandReviewEditMode.set(false);
  }

  private prefillEditableBrandFields(): void {
    const b = this.selectedBrand();
    const card = this.brandDetails();
    const cardOrEmpty: BrandDetailCardModel = card ?? { company: { industries: [] }, links: [] };
    this.manualBrandModel.set({
      companyName: cardOrEmpty.name ?? b?.name ?? '',
      website: cardOrEmpty.domain ?? b?.domain ?? '',
      description: marketingAboutFromCard(cardOrEmpty),
      logoUrl: b?.icon ?? '',
    });
    this.manualLogoEditorRevision.update((r) => r + 1);
  }

  private validateManualBrandFields(): { domainNorm: string; name: string; desc: string; logo: string } | null {
    const fields = [
      this.manualBrandForm.companyName(),
      this.manualBrandForm.website(),
      this.manualBrandForm.description(),
      this.manualBrandForm.logoUrl(),
    ];
    if (fields.some((c) => c.invalid())) {
      for (const c of fields) {
        c.markAsTouched();
      }
      return null;
    }

    return {
      domainNorm: this.normalizeWebsiteInput(this.manualBrandModel().website),
      name: this.manualBrandModel().companyName.trim(),
      desc: this.manualBrandModel().description.trim(),
      logo: this.manualBrandModel().logoUrl.trim(),
    };
  }

  onBrandSelect(brand: BrandSuggestionResponse): void {
    this.selectedBrand.set(brand);
    if (brand.brandId === MANUAL_BRAND_ID) {
      this.brandSelectionSource.set('manual');
      this.brandDetailFetchPending.set(false);
      this.brandDetails.set({
        description: '',
        company: { industries: [] },
        links: [],
      });
      this.slickOnboardService.brandDetails.set(brand);
      return;
    }

    this.brandSelectionSource.set('api');
    this.brandDetailFetchPending.set(true);
    this.brandFetchService.getBrandDetails(brand.brandId).subscribe({
      next: (response: unknown) => {
        const card = normalizeBrandfetchDetail(response);
        this.brandDetails.set(card);
        this.slickOnboardService.brandMarketingAbout.set(marketingAboutFromCard(card));
        this.slickOnboardService.brandDetails.set(brand);
        this.brandDetailFetchPending.set(false);
        this.gaService.trackOnboardingEvent('brand_details_brandfetch', {
          brand_domain: card.domain ?? brand.domain,
        });
      },
      error: () => {
        const card: BrandDetailCardModel = {
          description: '',
          company: { industries: [] },
          links: [],
        };
        this.brandDetails.set(card);
        this.slickOnboardService.brandMarketingAbout.set('');
        this.slickOnboardService.brandDetails.set(brand);
        this.brandDetailFetchPending.set(false);
      },
    });
  }

  onBrandSuggestionKeydown(event: KeyboardEvent, brand: BrandSuggestionResponse): void {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this.onBrandSelect(brand);
    }
  }

  clearSelectedBrand(): void {
    this.clearBrandSelectionState();
    this.manualBrandEntry.set(false);
    this.brandFetchModel.set({ domain: '' });
    this.brandSuggestions.set(undefined);
    this.domainSubject.next('');
  }

  editSelectedBrand(): void {
    if (!this.selectedBrand() || this.brandDetailFetchPending()) {
      return;
    }
    this.prefillEditableBrandFields();
    this.brandReviewEditMode.set(true);
  }

  cancelBrandEdit(): void {
    this.brandReviewEditMode.set(false);
  }

  applyBrandEdits(): void {
    const validated = this.validateManualBrandFields();
    if (!validated) {
      return;
    }

    const current = this.selectedBrand();
    if (!current) {
      return;
    }

    const { domainNorm, name, desc, logo } = validated;
    const existingCard = this.brandDetails();

    const updatedBrand: BrandSuggestionResponse = {
      ...current,
      name,
      domain: domainNorm,
      icon: logo || current.icon || MANUAL_BRAND_PLACEHOLDER_ICON,
    };

    this.selectedBrand.set(updatedBrand);
    this.slickOnboardService.brandDetails.set(updatedBrand);
    this.slickOnboardService.brandMarketingAbout.set(desc);
    this.brandDetails.set({
      ...existingCard,
      name,
      domain: domainNorm,
      description: desc,
      longDescription: '',
      company: existingCard?.company ?? { industries: [] },
      links: existingCard?.links ?? [],
    });
    this.brandReviewEditMode.set(false);
    this.brandFetchModel.set({ domain: domainNorm });
  }

  resetBrandSearch(): void {
    this.clearBrandSelectionState();
    this.manualBrandEntry.set(false);
    this.brandFetchModel.set({ domain: '' });
    this.brandSuggestions.set(undefined);
    this.domainSubject.next('');
    queueMicrotask(() => this.companyWebsiteInput?.nativeElement?.focus());
  }

  enterManualBrandMode(): void {
    this.manualBrandEntry.set(true);
    this.brandSuggestions.set(undefined);
    this.brandSearchLoading.set(false);
    const current = this.normalizeWebsiteInput(this.brandFetchModel().domain);
    this.manualBrandModel.set({
      companyName: '',
      website: current || '',
      description: '',
      logoUrl: '',
    });
    this.manualLogoEditorRevision.update((r) => r + 1);
  }

  /** From reviewed API match: go back to domain search with the same hostname. */
  trySearchAgainFromReview(): void {
    const domain = this.selectedBrand()?.domain;
    this.clearBrandSelectionState();
    this.manualBrandEntry.set(false);
    if (domain) {
      const norm = this.normalizeWebsiteInput(domain);
      this.brandFetchModel.set({ domain: norm });
      this.domainSubject.next(norm);
    } else {
      this.brandFetchModel.set({ domain: '' });
      this.domainSubject.next('');
    }
    queueMicrotask(() => this.companyWebsiteInput?.nativeElement?.focus());
  }

  /** From reviewed API match: open manual form prefilled from fetched profile. */
  switchToManualFromReview(): void {
    this.prefillEditableBrandFields();
    this.clearBrandSelectionState();
    this.manualBrandEntry.set(true);
  }

  onManualLogoUploaded(event: { file: string; fileKey: string; fullUrl: string }): void {
    const url = (event.fullUrl ?? '').trim();
    this.manualBrandModel.update((m) => ({ ...m, logoUrl: url }));
  }

  onManualLogoUrlBlur(): void {
    this.manualLogoEditorRevision.update((r) => r + 1);
  }

  switchToSearchMode(): void {
    this.manualBrandEntry.set(false);
    const w = this.normalizeWebsiteInput(this.manualBrandModel().website);
    if (w) {
      this.brandFetchModel.set({ domain: w });
    } else {
      this.brandFetchModel.set({ domain: this.brandFetchModel().domain });
    }
    this.domainSubject.next(this.brandFetchModel().domain);
  }

  applyManualBrand(): void {
    const validated = this.validateManualBrandFields();
    if (!validated) {
      return;
    }

    const { domainNorm, name, desc, logo } = validated;

    const synthetic: BrandSuggestionResponse = {
      brandId: MANUAL_BRAND_ID,
      claimed: false,
      domain: domainNorm,
      icon: logo || MANUAL_BRAND_PLACEHOLDER_ICON,
      name,
      qualityScore: 0,
      verified: false,
      _score: 0,
    };

    this.selectedBrand.set(synthetic);
    this.slickOnboardService.brandDetails.set(synthetic);
    this.slickOnboardService.brandMarketingAbout.set(desc);
    this.brandDetails.set({
      description: desc,
      company: { industries: [] },
      links: [],
    });
    this.brandSelectionSource.set('manual');
    this.manualBrandEntry.set(false);
    this.brandDetailFetchPending.set(false);
    this.brandFetchModel.set({ domain: domainNorm });
    this.gaService.trackOnboardingEvent('brand_details_manual', {
      brand_domain: domainNorm,
    });
  }

  onGoalSelect(goal: { id: number; name: string; description: string; color: string }): void {
    this.onboardingBrandService.goalInformation = goal;
    this.slickOnboardService.goalInformation.set(goal);
    this.gaService.trackOnboardingEvent('brand_details_goal', {
      goal_name: goal.name,
    });
  }

  getGoalClass(goal: { id: number; name: string; description: string; color: string }): string {
    if (this.onboardingBrandService.goalInformation?.id === goal.id) {
      return 'primary-go-button';
    }
    return 'secondary-button text-secondary';
  }

  saveBrandDetailsInfo(): void {
    if (this.brandReviewEditMode()) return;
    if (!this.slickOnboardService.brandCurrencyCode()) return;
    this.gaService.trackOnboardingEvent('brand_details_continue');
    this.stepComplete.emit(true);
  }
}
